import { ConnectError } from '@bufbuild/connect';
import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from '@/shared/hooks';
import { Button, errorToastCaller, Spinner, toastCaller } from '@/shared/ui';

import { authModel, StaffRef, useStaffSecurityStatus } from '@/entities/auth';
import { ModalID, modalModel } from '@/entities/modal';

import { SESSION_DATE_FORMAT } from '../../config';

import styles from './StaffPasswordResetPanel.module.scss';

type StaffPasswordResetPanelProps = {
  staff: StaffRef;
  className?: string;
  onRequired: () => void;
};

export const StaffPasswordResetPanel: FC<StaffPasswordResetPanelProps> = (
  props,
) => {
  const { className, staff, onRequired } = props;

  const { formatDate, formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const [isRequiring, setIsRequiring] = useState(false);

  const { request, status, setStatus, loadingState, isDenied, fetchStatus } =
    useStaffSecurityStatus(staff, (statusRequest) =>
      dispatch(
        authModel.thunks.staffPasswordResetStatus(statusRequest),
      ).unwrap(),
    );

  const handleRequire = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage({
            id: 'staffPasswordReset.requireConfirmTitle',
            defaultMessage: 'Require a new password?',
          }),
          description: formatMessage({
            id: 'staffPasswordReset.requireConfirmDescription',
            defaultMessage:
              'The current password stops working. The staff member is signed out on every device and integration, except sessions that work only in another clinic, and will get an email with a link to set a new password.',
          }),
          okText: formatMessage({
            id: 'staffPasswordReset.require',
            defaultMessage: 'Require a new password',
          }),
          danger: true,
          onConfirm: async () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            setIsRequiring(true);

            try {
              const response = await dispatch(
                authModel.thunks.staffPasswordResetRequire(request),
              ).unwrap();

              setStatus(response);
              onRequired();
              toastCaller({
                type: 'success',
                message: formatMessage({
                  id: 'staffPasswordReset.requireSuccess',
                  defaultMessage: 'A new password is required',
                }),
              });
            } catch (error) {
              errorToastCaller(error as ConnectError);
              await fetchStatus();
              onRequired();
            } finally {
              setIsRequiring(false);
            }
          },
        },
      }),
    );
  };

  if (loadingState === 'failed' && isDenied) {
    return null;
  }

  if (loadingState === 'failed') {
    return (
      <div
        className={cn(styles.container, className)}
        data-component="staff-password-reset-panel"
      >
        <p className={cn(styles.hint, 'p3t')}>
          <FormattedMessage
            id="staffPasswordReset.loadFailed"
            defaultMessage="The password status could not be loaded."
          />
        </p>

        <Button
          className={styles.retry}
          variant="tertiary"
          size="medium"
          onClick={fetchStatus}
        >
          <FormattedMessage id="global.retry" defaultMessage="Try again" />
        </Button>
      </div>
    );
  }

  if (!status) {
    return <Spinner className={styles.spinner} />;
  }

  return (
    <div
      className={cn(styles.container, className)}
      data-component="staff-password-reset-panel"
    >
      <div className={styles.text}>
        <p className="p2">
          {status.RequiredAt ? (
            <FormattedMessage
              id="staffPasswordReset.requiredSince"
              defaultMessage="A new password has been required since {date}"
              values={{
                date: formatDate(
                  status.RequiredAt.toDate(),
                  SESSION_DATE_FORMAT,
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id="staffPasswordReset.title"
              defaultMessage="Password"
            />
          )}
        </p>

        <p className={cn(styles.hint, 'p3t')}>
          {status.Required ? (
            <FormattedMessage
              id="staffPasswordReset.requiredHint"
              defaultMessage="The staff member cannot sign in until they set a new password through password recovery."
            />
          ) : (
            <FormattedMessage
              id="staffPasswordReset.hint"
              defaultMessage="Require a new password if you think someone else knows it. The staff member sets a new one through password recovery before the next sign-in."
            />
          )}
        </p>
      </div>

      {!status.Required && (
        <Button
          className={styles.action}
          variant="tertiary"
          size="medium"
          danger
          loading={isRequiring}
          onClick={handleRequire}
        >
          <FormattedMessage
            id="staffPasswordReset.require"
            defaultMessage="Require a new password"
          />
        </Button>
      )}
    </div>
  );
};
